import React from "react";
import { Percent } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { formatMoney } from "@/lib/pricing";

const ROW_STYLES = {
  big: { label: "font-heading font-bold text-base", val: "text-amber-400 font-black text-lg" },
  bold: { label: "font-semibold text-slate-200", val: "font-bold" },
  negative: { label: "text-rose-300", val: "text-rose-400 font-semibold" },
  plain: { label: "text-slate-400", val: "text-slate-300" },
};
const rowStyle = ({ big, bold, negative }) => ROW_STYLES[big ? "big" : bold ? "bold" : negative ? "negative" : "plain"];

export const Row = (props) => {
  const { label, val, negative } = props;
  const st = rowStyle(props);
  return (
    <div className="flex justify-between items-center" data-testid={negative ? "discount-applied-row" : undefined}>
      <span className={`text-sm ${st.label}`}>{label}</span>
      <span className={`font-mono-num ${st.val}`}>{val}</span>
    </div>
  );
};

function DiscountRow({ discount, setDiscount, sym }) {
  const setMode = (mode) => setDiscount((d) => ({ ...d, mode }));
  return (
    <div className="flex items-center justify-between gap-2 py-1" data-testid="discount-row">
      <span className="text-sm text-slate-400 flex items-center gap-1.5"><Percent className="w-3.5 h-3.5 text-rose-400" /> Discount 折扣</span>
      <div className="flex items-center gap-1.5">
        <div className="flex rounded-md border border-slate-700 bg-slate-950/60 p-0.5">
          <button data-on={discount.mode === "percent"} onClick={() => setMode("percent")} data-testid="discount-mode-percent"
            className="px-2 py-0.5 rounded text-[11px] font-bold text-slate-400 data-[on=true]:bg-rose-500 data-[on=true]:text-white transition-colors">%</button>
          <button data-on={discount.mode === "fixed"} onClick={() => setMode("fixed")} data-testid="discount-mode-fixed"
            className="px-2 py-0.5 rounded text-[11px] font-bold text-slate-400 data-[on=true]:bg-rose-500 data-[on=true]:text-white transition-colors">{sym}</button>
        </div>
        <Input type="number" min="0" step="0.01" value={discount.value} placeholder="0"
          onChange={(e) => setDiscount((d) => ({ ...d, value: e.target.value }))} data-testid="discount-value-input"
          className="bg-slate-950/60 border-slate-700 h-7 w-20 text-right text-sm font-mono-num px-2" />
      </div>
    </div>
  );
}

export default function QuoteTotals({ quote, sym, discount, setDiscount }) {
  const q = quote || {};
  return (
    <div className="p-5 border-t border-slate-800 space-y-2 bg-slate-950/30">
      <Row label="Respray 喷漆" val={formatMoney(sym, q.respraySubtotal || 0)} />
      <Row label="Panel Beating & Putty Realignment 钣金补土" val={formatMoney(sym, q.repairSubtotal || 0)} />
      <Row label="Tricoat Paint Upgrade 三层漆升级" val={formatMoney(sym, q.specialSubtotal || 0)} />
      <Row label="Services 服务" val={formatMoney(sym, q.servicesSubtotal || 0)} />
      <Separator className="bg-slate-800 my-1" />
      <Row label="Subtotal 小计" val={formatMoney(sym, q.subtotal || 0)} bold />
      <DiscountRow discount={discount} setDiscount={setDiscount} sym={sym} />
      {q.discountAmount > 0 && (
        <Row negative label={`Discount applied 已折扣${q.discountMode === "percent" ? ` (${q.discountValue}%)` : ""}`} val={`-${formatMoney(sym, q.discountAmount)}`} />
      )}
      {q.partsSubtotal > 0 && <Row label="Spare parts · net 零件 (不打折)" val={formatMoney(sym, q.partsSubtotal)} bold />}
      <Row label={`Tax 税 (${q.taxRate || 0}%)`} val={formatMoney(sym, q.tax || 0)} />
      <Separator className="bg-slate-800 my-1" />
      <Row label="TOTAL 合计" val={formatMoney(sym, q.total || 0)} big />
    </div>
  );
}
